import type { GeneratedDimension } from "building-types/dimensions/dimensionTypes";
import type { AssetSource, DxfAsset, ImageAsset, ImageAssetLegacy } from "building-types/entities/asset";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type {
  ArcOutputOptions,
  CircleOutputOptions,
  DefinedTextStyle,
  PolyLineOutputOptions,
  PolygonOutputOptions,
  TextOutputOptions,
  TextStyle,
} from "building-types/views/Primitive2DOutput";
import type { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import { PlacementXAnchor, PlacementYAnchor } from "common/utils/textAlignment";
import type { AABB2 } from "geom/aabb2Types";
import type { Mat3 } from "geom/mat3";
import type { OBB2 } from "geom/obb2";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { Vec2 } from "geom/vec2";
import type { SheetImageFilter } from "./SheetImageFilter";

export enum SheetGeometryType {
  POLYLINE = "POLYLINE",
  POLYGON = "POLYGON",
  TEXT = "TEXT",
  SVG = "SVG",
  ASSET = "ASSET",
  CIRCLE = "CIRCLE",
  ARC = "ARC",
}

export enum PlanRenderingMode {
  PDF = "PDF",
  DXF = "DXF",
}

export interface NoteLeaderArrowProps {
  readonly width: number;
  readonly length: number;
}

export interface DimensionProps {
  readonly startAnchor: Vec2;
  readonly endAnchor: Vec2;
  readonly baseDimLine: PolyLine2;
  readonly autoKind?: GeneratedDimension["placement"];
  readonly startExt?: number;
  readonly endExt?: number;
  readonly witnessLineGap?: number;
}

export type SheetGeometry =
  | SheetPolylineGeometry
  | SheetPolygonGeometry
  | SheetTextGeometry
  | SheetSVGGeometry
  | SheetAssetGeometry
  | SheetCircleGeometry
  | SheetArcGeometry;

export type GradientDescentRelaxationConstraintType = PolyLine2[];
export type GradientDescentRelaxationNoConstraintType = null;
export const GRADIENT_DESCENT_NO_CONSTRAINT: GradientDescentRelaxationNoConstraintType = null;
export type SharedGradientDescentRelaxationConstraintType =
  | GradientDescentRelaxationConstraintType
  | GradientDescentRelaxationNoConstraintType;

interface SheetGeometryBase<T extends SheetGeometryType> {
  readonly type: T;
  readonly handle?: DocEntityHandle;
  readonly groupId?: string;
  /** SCALING factor already folded into this geometry's sheet-inch fields. `makePlanGeometryList`
   * re-styles relaxed geometry, so the resolver applies `target / appliedScaleEffect`, not `target`. */
  readonly appliedScaleEffect?: number;
  /**
   * @TODO - Temporary property, to be removed after we have better control
   * of planVisibility on SheetGeometry
   */
  readonly buildingEntity?: any;

  // constraints that govern SheetGeometry relaxation
  readonly relaxationRangeAABB?: AABB2; // used by legacy relaxation
  readonly relaxationNormalX?: number; // used by legacy relaxation
  readonly relaxationNormalY?: number; // used by legacy relaxation

  readonly constraints?: PolyLine2[]; // Used by gradient descent relaxation
  /**
   * Used by gradient descent relaxation.
   * Shared constraints are grouped by a key, and a geometry may be in multiple groups.
   * If the value is GRADIENT_DESCENT_NO_CONSTRAINT, the geometry is constrained by other geometries in the same group,
   * but does not provide it's own constraints.
   */
  readonly sharedConstraints?: { [key: string]: SharedGradientDescentRelaxationConstraintType };

  readonly relaxationObstacleOBBs?: OBB2[]; // Used by gradient descent relaxation
  readonly dimensionProps?: DimensionProps; // Used by gradient descent relaxation
  readonly anchorPoint?: { x: number; y: number }; // Used by gradient descent relaxation, the point that remains fixed during relaxation
  readonly annotationNoteLeaderArrowProps?: NoteLeaderArrowProps; // Used by gradient descent relaxation, the arrow properties for annotation notes with leaders.

  readonly renderOrder?: number;

  readonly category?: SheetGeometryCategory;

  // custom string metadata tags that are currently only used for stylesheet applications
  readonly tags?: string[];
}

type PolylineAttributes = Omit<Partial<PolyLineOutputOptions> & Pick<PolyLineOutputOptions, "paths">, "layout">;

export interface SheetPolylineGeometry extends SheetGeometryBase<SheetGeometryType.POLYLINE>, PolylineAttributes {
  readonly renderFast?: boolean; // This should be deprecated in favor of a check in renderPDFPolylines
  readonly oneObstacle?: boolean; // If true the `paths` are used in relaxation using a bounding box around the whole `paths` not individual lines.
}

export function sheetPolylineGeometryNew(props: Omit<SheetPolylineGeometry, "type">): SheetPolylineGeometry {
  return {
    type: SheetGeometryType.POLYLINE,
    ...props,
  };
}

type PolygonAttributes = Omit<Partial<PolygonOutputOptions> & Pick<PolygonOutputOptions, "paths">, "layout">;
export interface SheetPolygonGeometry extends SheetGeometryBase<SheetGeometryType.POLYGON>, PolygonAttributes {}

export function sheetPolygonGeometryNew(props: Omit<SheetPolygonGeometry, "type">): SheetPolygonGeometry {
  return {
    type: SheetGeometryType.POLYGON,
    ...props,
  };
}

type TextAttributes = Partial<TextOutputOptions> & Pick<TextOutputOptions, "text" | "x" | "y">;
export interface SheetTextGeometry extends SheetGeometryBase<SheetGeometryType.TEXT>, TextAttributes {}

export type StyledSheetTextGeometry = Omit<SheetTextGeometry, keyof TextStyle> & DefinedTextStyle;

export function sheetTextGeometryNew(props: Omit<SheetTextGeometry, "type">): SheetTextGeometry {
  return {
    type: SheetGeometryType.TEXT,
    ...props,
  };
}

export function isSheetTextGeometry(entity: SheetGeometry): entity is SheetTextGeometry {
  return entity.type === SheetGeometryType.TEXT;
}

// The scale of SVG data inside PlanGeometryType.SVG is in PDF space.
export interface SheetSVGGeometry extends SheetGeometryBase<SheetGeometryType.SVG> {
  readonly x: number;
  readonly y: number;
  readonly path: string;
  readonly inPlanScale: boolean; // If true, the scale of SVG data is in plan space (world space when the plan is generated).
  readonly scale: number; // Only affects plan space geometry
}

export function sheetSVGGeometryNew(props: Omit<SheetSVGGeometry, "type" | "scale">): SheetSVGGeometry {
  return {
    type: SheetGeometryType.SVG,
    scale: 1,
    ...props,
  };
}

export const defaultPlacementXAnchor = PlacementXAnchor.CENTER;
export const defaultPlacementYAnchor = PlacementYAnchor.CENTER;

interface SheetAssetGeometryBase extends SheetGeometryBase<SheetGeometryType.ASSET> {
  readonly x: number;
  readonly y: number;
  readonly rotation?: number; // in radians.
  /**
   * Outer transform for asset geometry.
   * This transform is applied to the unpacked geometry at render time
   */
  readonly transform?: Mat3;

  readonly placementXAnchor?: PlacementXAnchor;
  readonly placementYAnchor?: PlacementYAnchor;
}

interface SheetImageAssetGeometryBase extends SheetAssetGeometryBase {
  readonly opacity?: number; // Only supported in realtime preview.
  readonly filter?: SheetImageFilter; // Only supported in realtime preview.
}

export interface SheetImageAssetLegacyGeometry extends SheetImageAssetGeometryBase {
  readonly assetType: ImageAssetLegacy["srcType"];
  readonly asset: AssetSource<ImageAssetLegacy>;

  // Image will be stretched to this width and height. Caller is responsible for ensuring correct aspect ratio.
  readonly width: number;
  readonly height: number;
}

interface SheetImageAssetGeometry extends SheetImageAssetGeometryBase {
  readonly assetType: ImageAsset["srcType"];
  readonly asset: AssetSource<ImageAsset>;
  readonly scale?: number;
}

export interface SheetDXFAssetGeometry extends SheetAssetGeometryBase {
  readonly assetType: DxfAsset["srcType"];
  readonly asset: AssetSource<DxfAsset>;
  readonly scale?: number;
}

export type SheetImageGeometry = SheetImageAssetGeometry | SheetImageAssetLegacyGeometry;
export type SheetCatalogAssetGeometry = SheetImageAssetGeometry | SheetDXFAssetGeometry;
export type SheetAssetGeometry = SheetImageGeometry | SheetDXFAssetGeometry;

export type SheetAssetGeometryProps =
  | Omit<SheetImageAssetGeometry, "type">
  | Omit<SheetImageAssetLegacyGeometry, "type">
  | Omit<SheetDXFAssetGeometry, "type">;

export function sheetAssetGeometryNew(props: SheetAssetGeometryProps): SheetAssetGeometry {
  return {
    type: SheetGeometryType.ASSET,
    ...props,
  };
}

export function isSheetAssetGeometry(entity: SheetGeometry): entity is SheetAssetGeometry {
  return entity.type === SheetGeometryType.ASSET;
}

type CircleAttributes = Partial<CircleOutputOptions> & Pick<CircleOutputOptions, "x" | "y" | "radius">;
export interface SheetCircleGeometry extends SheetGeometryBase<SheetGeometryType.CIRCLE>, CircleAttributes {}

export function sheetCircleGeometryNew(props: Omit<SheetCircleGeometry, "type">): SheetCircleGeometry {
  return {
    type: SheetGeometryType.CIRCLE,
    ...props,
  };
}

type ArcAttributes = Partial<ArcOutputOptions> & Pick<ArcOutputOptions, "x" | "y" | "xRadius" | "startRad" | "endRad">;
export interface SheetArcGeometry extends SheetGeometryBase<SheetGeometryType.ARC>, ArcAttributes {}

export function sheetArcGeometryNew(props: Omit<SheetArcGeometry, "type">): SheetArcGeometry {
  return {
    type: SheetGeometryType.ARC,
    clockwise: true,
    yRadius: props.xRadius,
    ...props,
  };
}

export interface SheetGeometryList {
  readonly geometry: SheetGeometry[];
}

// Sheet geometry list where items cannot be added or removed.
export interface ImmutableSheetGeometryList {
  readonly geometry: readonly SheetGeometry[];
}

export function sheetGeometryListNew(): SheetGeometryList {
  return {
    geometry: [],
  };
}

export function sheetAddGeometry(list: SheetGeometryList, geometry: SheetGeometry): void {
  list.geometry.push(geometry);
}

export function sheetAddGeometries(list: SheetGeometryList, geometries: readonly SheetGeometry[]): void {
  if (geometries.length < 50000) {
    list.geometry.push(...geometries);
  } else {
    for (const geometry of geometries) {
      sheetAddGeometry(list, geometry);
    }
  }
}

export function isSheetPolylineGeometry(geometry: SheetGeometry): geometry is SheetPolylineGeometry {
  return geometry.type === SheetGeometryType.POLYLINE;
}

export function isSheetGeometryLineBased(
  geometry: SheetGeometry,
): geometry is SheetPolylineGeometry | SheetPolygonGeometry | SheetCircleGeometry | SheetArcGeometry {
  return (
    geometry.type === SheetGeometryType.POLYLINE ||
    geometry.type === SheetGeometryType.POLYGON ||
    geometry.type === SheetGeometryType.CIRCLE ||
    geometry.type === SheetGeometryType.ARC
  );
}

export function isSheetGeometryTextBased(geometry: SheetGeometry): geometry is SheetTextGeometry {
  return geometry.type === SheetGeometryType.TEXT;
}
